// WebSocket connection service with shell bridge support

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3000/ws';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isAuthenticated = false;
    this.currentToken = null;
    this.iframeWindow = null;
  }

  /**
   * Connect to WebSocket server
   */
  connect(token) {
    return new Promise((resolve, reject) => {
      try {
        // Prevent duplicate connections
        if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
          console.warn('[Shell WS] Already connected or connecting. Ignoring duplicate connection attempt.');
          resolve();
          return;
        }

        this.currentToken = token;
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('[Shell WS] Connected');
          this.reconnectAttempts = 0;
          this.emit('connected');

          // Send authentication immediately
          this.authenticate(token);
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error('[Shell WS] Error:', error);
          this.emit('error', { message: 'WebSocket connection error' });
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('[Shell WS] Closed:', event.code, event.reason);
          this.isAuthenticated = false;
          this.emit('disconnected', { code: event.code, reason: event.reason });

          // Don't reconnect on:
          // - Normal closure (1000)
          // - Duplicate connection (4005) - this means another instance is already connected
          const shouldNotReconnect = event.code === 1000 || event.code === 4005;

          if (!shouldNotReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnect();
          } else if (event.code === 4005) {
            console.warn('[Shell WS] Duplicate connection detected. Not reconnecting.');
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Send authentication message
   */
  authenticate(token) {
    const authMessage = {
      BLOCK_TYPE: 'SYSTEM',
      ACTION: 'AUTH',
      TOKEN: token
    };

    this.send(authMessage);
  }

  /**
   * Handle incoming messages
   */
  handleMessage(data) {
    try {
      const message = JSON.parse(data);
      const { BLOCK_TYPE, ACTION, STATUS } = message;

      console.log('[Shell WS] Received:', message);

      // Handle authentication response
      if (BLOCK_TYPE === 'SYSTEM' && ACTION === 'AUTH') {
        if (STATUS === 'SUCCESS') {
          this.isAuthenticated = true;
          this.emit('authenticated');

          // Check paired status after authentication
          this.checkPairedStatus();
        } else {
          this.emit('authFailed', { error: message.ERROR || 'Authentication failed' });
        }
        return;
      }

      // Handle paired status response
      if (BLOCK_TYPE === 'SYSTEM' && ACTION === 'PAIRED_STATUS') {
        const isConnected = STATUS === 'CONNECTED';
        this.emit('pairedStatusChanged', { isConnected });
        return;
      }

      // Relay all messages to iframe (if attached)
      if (this.iframeWindow) {
        this.iframeWindow.postMessage({ type: 'ws/message', payload: message }, window.location.origin);
      }

      // Handle other message types
      if (BLOCK_TYPE === 'DATA') {
        this.emit('dataReceived', message.DATA);
      } else if (BLOCK_TYPE === 'COMMAND') {
        this.emit('commandReceived', message);
      } else if (BLOCK_TYPE === 'BIOMETRY') {
        this.emit('biometryReceived', message);
      }
    } catch (error) {
      console.error('[Shell WS] Failed to parse message:', error);
      this.emit('error', { message: 'Failed to parse message' });
    }
  }

  /**
   * Send message to server
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const data = JSON.stringify(message);
      this.ws.send(data);
      console.log('[Shell WS] Sent:', message);
      return true;
    }
    console.warn('[Shell WS] Not connected, message not sent');
    return false;
  }

  /**
   * Check paired status
   */
  checkPairedStatus() {
    const message = {
      BLOCK_TYPE: 'SYSTEM',
      ACTION: 'PAIRED_STATUS'
    };
    this.send(message);
  }

  /**
   * Attach iframe window for message relay
   */
  attachIframe(iframeContentWindow) {
    this.iframeWindow = iframeContentWindow;
    console.log('[Shell WS] Iframe attached');

    // Notify iframe that shell is ready
    if (this.iframeWindow) {
      this.iframeWindow.postMessage({ type: 'shell/ready' }, window.location.origin);
    }
  }

  /**
   * Detach iframe window
   */
  detachIframe() {
    this.iframeWindow = null;
    console.log('[Shell WS] Iframe detached');
  }

  /**
   * Handle messages from iframe
   */
  handleIframeMessage(message) {
    if (message.type === 'child/ws/send') {
      this.send(message.payload);
    } else if (message.type === 'child/subscribe') {
      // Iframe is requesting subscription to WS messages
      console.log('[Shell WS] Child subscribed');
    }
  }

  /**
   * Send data message
   */
  sendData(data) {
    if (!this.isAuthenticated) {
      console.warn('[Shell WS] Not authenticated');
      return false;
    }

    const message = {
      BLOCK_TYPE: 'DATA',
      DATA: data
    };
    return this.send(message);
  }

  /**
   * Send command message
   */
  sendCommand(activityId, commandId) {
    if (!this.isAuthenticated) {
      console.warn('[Shell WS] Not authenticated');
      return false;
    }

    const message = {
      BLOCK_TYPE: 'COMMAND',
      ACTIVITY_ID: activityId,
      COMMAND_ID: commandId
    };
    return this.send(message);
  }

  /**
   * Reconnect with exponential backoff
   */
  reconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[Shell WS] Reconnecting in ${delay}ms... (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.currentToken) {
        this.connect(this.currentToken);
      }
    }, delay);
  }

  /**
   * Disconnect
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
      this.isAuthenticated = false;
      this.currentToken = null;
      this.iframeWindow = null;
    }
  }

  /**
   * Event listener management
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  /**
   * Get connection state
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
